/* prettier-ignore */

import { Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, catchError, map, of, throwError } from "rxjs";
import { CiceroneData, Device } from "../interfaces/myCustomRoute.interface";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { DEFAULT_IMAGE_VARIANT_WIDTH } from "../shared/image-variants/config/constants";
import { buildItremThumbnailUrl, buildVariantUrl, isPlaceholderImage, isVariantUrl } from "../shared/image-variants/helpers/image-variants";
import { readConnectionMaxWidth } from "../shared/image-variants/helpers/connection-quality";
import { ImageVariantWidth } from "../shared/image-variants/interfaces";

@Injectable({
  providedIn: "root",
})

export class RestService {
  ttlTwoHours: number = 1000 * 3600 * 2; //2horas
  apiUrlBase: string = environment.apiUrlBase;
  
  ciceroneData: CiceroneData;
  private _device = signal<Device | null>(null);
  readonly device = this._device.asReadonly();
  subDomain?: any;
  expiredLicense = signal(false);
  token: any;

  constructor(
    public http: HttpClient, 
    private router: Router) 
  {}

  setDevice(res): void {
    this._device.set({
      distributorID: res?.distributorID,
      type: res?.type ?? "app",
      channel: res?.channelID,
      client: res?.clientID,
      chatbot: res?.chatbot,
      logo: res?.appStyle?.logo,
      logoFooter: res?.appStyle?.logoList?.footerLogo ?? undefined,
      languages: res?.lang?.availableLanguages,
      mainLanguage: res?.lang?.mainLanguage,
      headerLangType: res?.lang?.styleLanguage ?? 'flags',
      showGoogleLangs: res?.lang?.automaticTrad ?? true,
      location: {
        latitude: parseFloat(res?.location?.lat),
        longitude: parseFloat(res?.location?.lng),
        address: res?.location?.address,
        searchRadius: Number(res?.location?.searchRadius) ?? 0.5
      },
      style: {
        colorCorp1: res?.appStyle?.corpColor1,
        colorCorp2: res?.appStyle?.corpColor2,
        corpCompl: res?.appStyle?.corpCompl,
        headerColor: res?.appStyle?.headerColor,
        backgroundColor: res?.appStyle?.backgroundColor,
      },
      appID: res?.appId,
      privacyPolicy: res?.privacyPolicy,
      // passport: res?.passport
      hasIncidentsSurvey: res?.hasIncidentsSurvey, // TODO: AGREGAR A SSR
      socialLogin: {
        google: res?.socialLogin?.google ?? false,
        meta: res?.socialLogin?.meta ?? false,
        apple: res?.socialLogin?.apple ?? false,
      },
      uppercaseTitles: res?.uppercaseTitles === true,
    });
  }

  updateDevice(partial: Partial<Device>): void {
    this._device.update(d => d ? { ...d, ...partial } : d);
  }

  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //------------------------------------------------------------ INCREMENTO CLICKS -------------------------------------------------------------------
  increaseVisitsPoint(id): Observable<any> {
    return this.http.post(`${this.apiUrlBase}/api/increase-point-views/${id}`, {}).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  increaseVisitsEvent(id): Observable<any> {
    return this.http.post(`${this.apiUrlBase}/api/increase-event-views/${id}`, {}).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }
  
  increaseVisitsHiking(id): Observable<any> {
    return this.http.post(`${this.apiUrlBase}/api/increase-hiking-views/${id}`, {}).pipe(
      catchError((err) => { 
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  increaseVisitsPdf(id): Observable<any> {
    return this.http.post(`${this.apiUrlBase}/api/increase-pdf-views/${id}`, {}).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }
  //--------------------------------------------------------------------------------------------------------------------------------------------------
  //------------------------------------------------------------ API PWA ENDPOINTS -------------------------------------------------------------------

  disableRecommendedRoute(id): Observable<any> {
    return this.http.get(`${this.apiUrlBase}/api/change-route-status/${id}`).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  getHomeData(): Observable<any> {
    return this.http.get(`${this.apiUrlBase}/api/get-home-app-data/${this.subDomain}`).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response);
        this.addThumbnail(response);
        return response;
      })
    );
  }

  getDetailsPoint(id: string): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-point-app-details/${id}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response);
        return response;
      })
    );
  }

  getDetailsEvent(id: string): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-event-app-details/${id}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response);
        return response;
      })
    );
  }

  getListPoints(id: string): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-categories-app-list/${id}/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        // return throwError(() => err);
        return throwError(() => new Error('getListPoints HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items);
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }

  getListEvents(): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-events-list/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items);
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }
  
  getListHikings(): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-hiking-list/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items); 
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }

  getListOffers(): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-offers-list/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items);
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }

  getCommonApp(token = null): Observable<any> {
    let url = `${this.apiUrlBase}/api/get-common-app/${this.subDomain}`;
    if (token)
      url = `${this.apiUrlBase}/api/get-common-app/${this.subDomain}/${token}`;

    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  getItemsSearchEngineHomeApp(): Observable<any> {
    return this.http.get(`${this.apiUrlBase}/api/get-items-search-engine-home-app/${this.subDomain}`).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  getFavourites(): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-favourites-app/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  deleteAcount(user_id): any {
    return this.http.post(`${this.apiUrlBase}/api/delete-user-login`, { user_id }).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  getRoute(id): any {
    return this.http.post(`${this.apiUrlBase}/api/get-route-app/${id}`, {}).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  getSurvey(id): any {
    return this.http.post(`${this.apiUrlBase}/api/get-cicerone-survey-pwa/${id}`, {}).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  saveSurvey(data): any {
    return this.http.post(`${this.apiUrlBase}/api/pwa-save-survey`, { data }).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  sendStats(sesh: any) {
    return this.http.post(`${this.apiUrlBase}/api/send-stats`, { sesh }).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  /** Geo de visitante (ipapi) en tabla dedicada web_visitor_geolocations. */
  sendWebVisitorGeolocation(payload: {
    client_id?: number | null;
    chatbot_id?: number | string | null;
    subdomain?: string | null;
    session_id?: string | null;
    source?: 'web_cicerone' | 'web_embed';
    language?: string | null;
    page_host?: string | null;
    geolocation: Record<string, unknown>;
  }) {
    return this.http.post(`${this.apiUrlBase}/api/web-visitor-geolocation`, payload).pipe(
      catchError((err) => {
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  handleNotFound(err) {
    if (this.router.url.includes('survey-id'))
      return;

    if (err.status === 401)
      this.expiredLicense.set(true);

    if (err.status === 404 || err.status === 500 || err.status === 401) {
      this.router.navigateByUrl("not-found");
    }
  }

  getDistributorHome() {
    return this.http.get(`${this.apiUrlBase}/api/get-home-community-data/${this.subDomain}`).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response.data.homePoints);
        this.addThumbnail(response.data.homePoints);
        this.replaceUrls(response.data.appList);
        this.addThumbnail(response.data.appList);
        this.replaceUrls(response.data.heroSection);
        this.addThumbnail(response.data.heroSection);
        let events = response.data?.homePoints?.find(e => e.type === 'events')?.items;
        if (events) events = this.shuffleArray(events)
        return response;
      }),
    )
  }

  getDistributorCategoriesList(id: string): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-categories-community-app-list/${id}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items);
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }

  getDistributorListEvents(): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-list-events/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items);
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }
  
  getDistributorListHikings(): Observable<any> {
    const url = `${this.apiUrlBase}/api/get-list-hikings/${this.subDomain}`;
    return this.http.get(url).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      }),
      map((response: any) => {
        this.replaceUrls(response?.items);
        this.addThumbnail(response?.items);
        return response;
      })
    );
  }

  getRouteDistributor(id): any {
    return this.http.post(`${this.apiUrlBase}/api/get-route-community/${id}`, {}).pipe(
      catchError((err) => {
        this.handleNotFound(err);
        return throwError(() => new Error('HTTP request failed', { cause: err }));
      })
    );
  }

  getAudioFromApi(body: { region: string; phrase: string; file?: boolean }) {
    return this.http.post('https://node.ciceroneweb.com/text-to-speech', body, {
      responseType: 'blob'
    });
  }

  //----------------------------------------------------------------------------------------------------------------------------------------------------------
  //-------------------- AUX FUNCTIONS -----------------------------------------------------------------------------------------------------------------------
  //----------------------------------------------------------------------------------------------------------------------------------------------------------
  replaceUrls(object: any) {
    return;
    for (const key in object) {
      if (typeof object[key] === "string") {
        object[key] = this.replaceUrlLocal(object[key]);
        object[key] = this.replaceUrl(object[key]);
      } else if (typeof object[key] === "object") {
        this.replaceUrls(object[key]);
      }
    }
  }

  /**
   * HRS-3471: rewrites `image` (from `image ?? cover_image`) to ONE width variant
   * and keeps the original in `imageFallback`; `default.webp` -> `null`. URLs
   * without CMS variants are left untouched. Variant URLs are built only in
   * `shared/image-variants`; the width is capped by the connection tier.
   */
  addThumbnail(object: any, width: ImageVariantWidth = DEFAULT_IMAGE_VARIANT_WIDTH) {
    if (!object || typeof object !== "object") return;

    const imageValue = object.image;
    const coverValue = object.cover_image;
    // HRS-2609: an ITREM thumbnail of the fallback counts as processed too (else the thumbnail becomes the fallback)
    const alreadyProcessed = typeof object.imageFallback === "string"
      && (isVariantUrl(imageValue) || buildItremThumbnailUrl(object.imageFallback) === imageValue);

    if (!alreadyProcessed && (typeof imageValue === "string" || typeof coverValue === "string")) {
      if (typeof imageValue === "string" && isPlaceholderImage(imageValue)) object.image = null;
      if (typeof coverValue === "string" && isPlaceholderImage(coverValue)) object.cover_image = null;

      const original = [imageValue, coverValue].find((value) => typeof value === "string" && !isPlaceholderImage(value));
      if (original) {
        const maxWidth = readConnectionMaxWidth();
        const cappedWidth = maxWidth ? (Math.min(width, maxWidth) as ImageVariantWidth) : width;
        object.imageFallback = original;
        object.image = buildVariantUrl(original, cappedWidth) ?? buildItremThumbnailUrl(original) ?? original;
      }
    }

    for (const key in object) {
      if (object[key] && typeof object[key] === "object") {
        this.addThumbnail(object[key], width);
      }
    }
  }

  replaceUrl(valor: string): string {
    return valor.replace("https://experimental.ciceroneweb.com/storage/", "https://cicerone.cms-iurban.com/storage/");
  }

  replaceUrlLocal(valor: string): string {
    return valor.replace("http://cicerone-back.test/storage/", "https://cicerone.cms-iurban.com/storage/");
  }

  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }
  
}